import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTheme } from 'next-themes'
import { Heart, AlertCircle, CheckCircle, Calendar } from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import { useDonation } from '../contexts/DonationContext'
import { useDonationPayment } from '../contexts/DonationPaymentContext'
import { handleSquareError } from '../../secret/lib/square'
import ProgressBar from '../components/ProgressBar'
import DonationTiers from '../components/DonationTiers'
import TipSelector from '../../secret/components/checkout/TipSelector'
import ReservationTimer from '../../secret/components/checkout/ReservationTimer'
import DonationPaymentForm from '../components/DonationPaymentForm'
import Footer from '../../components/Footer'
import ThemeToggle from '../../components/ThemeToggle'

import useAsset from '../../hooks/useAsset'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../../secret/components/ui/EnterpriseToast'

// ============================================================================
// CAMPAIGN MODE TOGGLE
// 'active' = Active campaign with specific goal (e.g., Thanksgiving 2025)
// 'future' = Between campaigns - donations go toward future events
// ============================================================================
const CAMPAIGN_MODE: 'active' | 'future' = 'future'

const DonatePage: React.FC = () => {
  const navigate = useNavigate()
  const { resolvedTheme } = useTheme()
  const {
    amount,
    setAmount,
    tipAmount,
    setTipAmount,
    totalAmount,
    donorEmail,
    setDonorEmail,
    donorName,
    setDonorName,
    reservation,
    createReservation,
    getTailsFunded,
    refreshProgress,
  } = useDonation()

  const { initializePayment, processCardPayment, processApplePayment, processGooglePayment, setError: setPaymentError } = useDonationPayment()

  // Cache-busted asset URLs
  const logoWhite = useAsset('/tailtations-white.svg')
  const logoDark = useAsset('/tailtations.svg')
  const repeatBg = useAsset('/repeat-bg.png')

  const [step, setStep] = useState<'select' | 'details' | 'payment' | 'success'>('select')
  const [donationId, setDonationId] = useState<string | null>(null)
  const [donationNumber, setDonationNumber] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isCreatingReservation, setIsCreatingReservation] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [step])

  // Lock/unlock page scrolling when sidebar opens/closes
  useEffect(() => {
    if (isMobileSidebarOpen) {
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.top = `-${window.scrollY}px`
      document.body.style.width = '100%'
    } else {
      const scrollY = document.body.style.top
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1)
      }
    }

    return () => {
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
    }
  }, [isMobileSidebarOpen])

  // Validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Navigation handlers
  const handleCateringClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsMobileSidebarOpen(false)
    navigate('/catering')
  }

  const handleAboutClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsMobileSidebarOpen(false)
    navigate('/')
    setTimeout(() => {
      document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsMobileSidebarOpen(false)
    navigate('/')
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  // Handle continue to details
  const handleContinueToDetails = async () => {
    if (amount === 0) {
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          icon={<AlertCircle />}
          title="Amount Required"
          message="Please select a donation amount"
          type="error"
          visible={t.visible}
        />
      ))
      return
    }
    setStep('details')
  }

  // Handle continue to payment
  const handleContinueToPayment = async () => {
    if (!donorEmail) {
      setEmailError('Email is required for your donation receipt')
      document.getElementById('email-input-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    if (!validateEmail(donorEmail)) {
      setEmailError('Please enter a valid email address')
      document.getElementById('email-input-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    setEmailError(null)
    setIsCreatingReservation(true)

    try {
      await createReservation(amount)
      // Move to payment step first, then initialize payment after DOM renders
      setStep('payment')
    } catch (error: any) {
      console.error('Error creating reservation:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          icon={<AlertCircle />}
          title="Reservation Failed"
          message={error.message || 'Failed to create reservation. Please try again.'}
          type="error"
          visible={t.visible}
        />
      ))
    } finally {
      setIsCreatingReservation(false)
    }
  }

  // Initialize payment form when entering payment step
  useEffect(() => {
    if (step === 'payment' && reservation) {
      // Small delay to ensure DOM is rendered
      const timeoutId = setTimeout(() => {
        initializePayment('donation-card-container', Math.round(totalAmount * 100))
          .catch(error => {
            console.error('Payment initialization failed:', error)
            toast.custom((t) => (
              <EnterpriseToast
                id={t.id}
                icon={<AlertCircle />}
                title="Payment Form Error"
                message="Failed to initialize payment form. Please try again."
                type="error"
                visible={t.visible}
              />
            ))
          })
      }, 100)

      return () => clearTimeout(timeoutId)
    }
  }, [step, reservation, totalAmount, initializePayment])

  // Handle reservation expiration
  const handleReservationExpire = () => {
    toast.custom((t) => (
      <EnterpriseToast
        id={t.id}
        icon={<AlertCircle />}
        title="Reservation Expired"
        message="Your reservation has expired. Please try again."
        type="error"
        visible={t.visible}
      />
    ))
    setStep('select')
  }

  // Process payment
  const handleProcessPayment = async (paymentMethod: 'card' | 'apple' | 'google') => {
    if (!reservation) {
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          icon={<AlertCircle />}
          title="No Reservation"
          message="No reservation found. Please try again."
          type="error"
          visible={t.visible}
        />
      ))
      return
    }

    setIsProcessing(true)

    try {
      // For Apple Pay and Google Pay, tokenize FIRST (must happen immediately after user gesture)
      // Then do backend work. For card payments, we can do backend work first.
      let paymentResult
      
      if (paymentMethod === 'apple') {
        // Apple Pay requires immediate tokenization after click
        paymentResult = await processApplePayment(Math.round(totalAmount * 100), reservation.reservation_id)
        if (paymentResult?.errors) {
          const errorMessage = handleSquareError(paymentResult.errors)
          console.error('Square tokenization errors:', paymentResult.errors)
          throw new Error(errorMessage)
        }
      } else if (paymentMethod === 'google') {
        // Google Pay also requires immediate tokenization after click
        paymentResult = await processGooglePayment(Math.round(totalAmount * 100), reservation.reservation_id)
        if (paymentResult?.errors) {
          const errorMessage = handleSquareError(paymentResult.errors)
          console.error('Square tokenization errors:', paymentResult.errors)
          throw new Error(errorMessage)
        }
      }

      // Now do the backend work (convert reservation to donation)
      const convertResponse = await fetch('/.netlify/functions/convert-reservation-to-donation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reservationId: reservation.reservation_id,
          donorEmail: donorEmail.toLowerCase().trim(),
          donorName: donorName.trim(),
          amount,
          tipAmount,
        }),
      })

      if (!convertResponse.ok) {
        const errorData = await convertResponse.json()
        throw new Error(errorData.error || 'Failed to create donation')
      }

      const { donation_id, donation_number } = await convertResponse.json()
      setDonationId(donation_id)
      setDonationNumber(donation_number)

      // For card payments, tokenize after backend work (no user gesture requirement)
      if (paymentMethod === 'card') {
        paymentResult = await processCardPayment(Math.round(totalAmount * 100), donation_id)
        if (paymentResult?.errors) {
          const errorMessage = handleSquareError(paymentResult.errors)
          console.error('Square tokenization errors:', paymentResult.errors)
          throw new Error(errorMessage)
        }
      }

      const paymentResponse = await fetch('/.netlify/functions/process-donation-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceId: paymentResult?.token,
          amount: totalAmount,
          donationId: donation_id,
          donorEmail: donorEmail.toLowerCase().trim(),
          verificationToken: paymentResult?.verificationToken,
          donorTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      })

      if (!paymentResponse.ok) {
        const errorData = await paymentResponse.json()
        throw new Error(errorData.error || 'Payment processing failed')
      }

      await refreshProgress()
      setStep('success')

      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          icon={<CheckCircle />}
          title="Donation Successful!"
          message={`Thank you for your generosity! Confirmation sent to ${donorEmail}`}
          type="success"
          visible={t.visible}
        />
      ))
    } catch (error: any) {
      console.error('Payment processing error:', error)
      const errorMessage = error.message || 'Unable to process payment. Please try again.'

      // Set error inline in the payment form
      setPaymentError(errorMessage)

      // Also show toast for visibility
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          icon={<AlertCircle />}
          title="Payment Failed"
          message={errorMessage}
          type="error"
          visible={t.visible}
        />
      ))
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>{CAMPAIGN_MODE === 'future' ? 'Donate - Support Future Campaigns | Tailtations' : 'Donate - Feed Skid Row This Thanksgiving | Tailtations'}</title>
        <meta name="description" content={CAMPAIGN_MODE === 'future' ? 'Support Tailtations charitable events. Your donation helps us bring dignity and joy to communities in need with gourmet lobster tails.' : 'Help us serve 240 FREE gourmet lobster tails to people in need on Thanksgiving morning in Skid Row, Los Angeles.'} />
      </Helmet>

      <div
        className="min-h-screen bg-background relative"
        style={{ scrollBehavior: 'smooth' }}
      >
        {/* Subtle Background Pattern */}
        <div
          className="fixed inset-0 opacity-5 pointer-events-none z-0"
          style={{
            backgroundImage: `url(${repeatBg})`,
            backgroundRepeat: 'repeat',
            backgroundSize: '200px',
          }}
        />

        {/* Grid Background Overlay */}
        <div
          className="fixed inset-0 opacity-20 pointer-events-none z-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(138, 49, 52, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(138, 49, 52, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
        />

        {/* Global Header - Matching tailtations.com */}
        <header
          className="bg-background py-8 px-8 border-b-2 relative z-10"
          style={{ borderBottomColor: '#8a3134' }}
        >
          <div className="container mx-auto flex justify-between items-center max-w-7xl">
            {/* Desktop Navigation - Left */}
            <div className="hidden md:flex items-center">
              <button
                onClick={handleCateringClick}
                className="px-4 py-2.5 text-xl font-semibold font-heading border border-primary dark:border-gray-400 text-gray-900 dark:text-foreground rounded-md hover:bg-muted transition-colors"
                style={{ lineHeight: '1' }}
              >
                Event Catering
              </button>
            </div>

            {/* Logo - Center */}
            <div className="flex-1 flex justify-center">
              <Link to="/">
                <img
                  src={resolvedTheme === 'dark' ? logoWhite : logoDark}
                  alt="Tailtations Fried Lobster Brand"
                  className="w-[250px] h-auto"
                />
              </Link>
            </div>

            {/* Desktop Navigation - Right */}
            <div className="hidden md:flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleAboutClick}
                  className="px-4 py-2.5 text-xl font-semibold font-heading border border-primary dark:border-gray-400 text-gray-900 dark:text-foreground rounded-md hover:bg-muted transition-colors"
                  style={{ lineHeight: '1' }}
                >
                  About
                </button>
                <button
                  onClick={handleContactClick}
                  className="px-4 py-2.5 text-xl font-semibold font-heading border border-primary dark:border-gray-400 text-gray-900 dark:text-foreground rounded-md hover:bg-muted transition-colors"
                  style={{ lineHeight: '1' }}
                >
                  Contact
                </button>
              </div>
              <div className="ml-4">
                <ThemeToggle />
              </div>
            </div>

            {/* Mobile Hamburger Menu */}
            <div className="md:hidden flex items-center">
              {!isMobileSidebarOpen && (
                <button
                  onClick={() => setIsMobileSidebarOpen(true)}
                  className="flex items-center justify-center w-10 h-10 border border-border text-foreground rounded-md hover:bg-muted transition-colors"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Mobile Sidebar */}
        <div className={`fixed inset-0 z-50 md:hidden ${isMobileSidebarOpen ? 'block' : 'hidden'}`}>
          <div
            className="fixed inset-0 bg-black"
            style={{ opacity: 0.5 }}
            onClick={() => setIsMobileSidebarOpen(false)}
          />
          <div
            className={`fixed top-0 right-0 h-full w-80 bg-background shadow-lg transform transition-transform duration-300 ease-in-out ${
              isMobileSidebarOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className="flex flex-col h-full">
              <div className="flex justify-end p-2">
                <button
                  onClick={() => setIsMobileSidebarOpen(false)}
                  className="flex items-center justify-center w-10 h-10 border border-border text-foreground rounded-md hover:bg-muted transition-colors"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="flex justify-center pt-2 pb-4 border-b-2" style={{ borderBottomColor: '#8a3134' }}>
                <img
                  src={resolvedTheme === 'dark' ? logoWhite : logoDark}
                  alt="Tailtations Fried Lobster Brand"
                  className="w-[200px] h-auto"
                />
              </div>
              <nav className="flex-1 px-8 py-8">
                <div className="space-y-6">
                  <button
                    onClick={handleCateringClick}
                    className="w-full px-6 py-3 text-xl font-semibold font-heading border border-primary dark:border-gray-400 text-gray-900 dark:text-foreground rounded-md hover:bg-muted transition-colors"
                  >
                    Event Catering
                  </button>
                  <button
                    onClick={handleAboutClick}
                    className="w-full px-6 py-3 text-xl font-semibold font-heading border border-primary dark:border-gray-400 text-gray-900 dark:text-foreground rounded-md hover:bg-muted transition-colors"
                  >
                    About
                  </button>
                  <button
                    onClick={handleContactClick}
                    className="w-full px-6 py-3 text-xl font-semibold font-heading border border-primary dark:border-gray-400 text-gray-900 dark:text-foreground rounded-md hover:bg-muted transition-colors"
                  >
                    Contact
                  </button>
                  <div className="pt-4">
                    <ThemeToggle />
                  </div>
                </div>
              </nav>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="w-full relative z-10 bg-background">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: 'url(/turktations-repeating-bg.png)',
              backgroundRepeat: 'repeat',
              opacity: 0.2,
              mixBlendMode: 'multiply'
            }}
          />
          <div className="container mx-auto px-4 py-16 max-w-6xl relative z-10">
          {step === 'select' && (
            <>
              {/* ============================================================================ */}
              {/* FUTURE CAMPAIGNS MODE - Shown when CAMPAIGN_MODE = 'future' */}
              {/* ============================================================================ */}
              {CAMPAIGN_MODE === 'future' ? (
                <>
                  {/* Hero Section - Campaign Concluded */}
                  <div className="text-center mb-16">
                    <h1
                      className="text-5xl md:text-7xl font-bold mb-6 font-heading text-primary dark:text-white"
                    >
                      Thank You for Feeding Skid Row This Thanksgiving
                    </h1>
                    <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-8 font-bold">
                      Together, we served deep fried lobster tails to those in need.
                    </p>
                    <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed font-bold">
                      On Thanksgiving morning, November 27th, 2025, we brought dignity and joy to Skid Row, Los Angeles,
                      by serving premium deep-fried lobster tails to those experiencing homelessness.
                      Thank you to everyone who made this possible!
                    </p>
                  </div>

                  {/* Progress Bar - Shows final results */}
                  <div className="mb-16">
                    <ProgressBar />
                  </div>

                  {/* Campaign Summary - Concluded Version */}
                  <div
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-10 mb-16"
                    style={{ border: '3px solid #DAA520' }}
                  >
                    <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center font-heading">
                      Campaign Summary
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                      <div className="flex flex-col items-center text-center">
                        <div className="bg-[#8B3A3A] rounded-full p-4 mb-4">
                          <Heart className="w-8 h-8 text-white" />
                        </div>
                        <div className="font-semibold text-gray-900 dark:text-white text-lg mb-1">Cost Per Tail</div>
                        <div className="text-gray-600 dark:text-gray-400">$8.40 per lobster tail</div>
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <div className="bg-[#8B3A3A] rounded-full p-4 mb-4">
                          <Heart className="w-8 h-8 text-white" />
                        </div>
                        <div className="font-semibold text-gray-900 dark:text-white text-lg mb-1">Retail Value</div>
                        <div className="text-gray-600 dark:text-gray-400">$5,077.15 worth of food</div>
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <div className="bg-[#DAA520] rounded-full p-4 mb-4">
                          <Calendar className="w-8 h-8 text-white" />
                        </div>
                        <div className="font-semibold text-gray-900 dark:text-white text-lg mb-1">Event Date</div>
                        <div className="text-gray-600 dark:text-gray-400">November 27, 2025 ✓</div>
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <div className="bg-[#8B3A3A] rounded-full p-4 mb-4">
                          <Heart className="w-8 h-8 text-white" />
                        </div>
                        <div className="font-semibold text-gray-900 dark:text-white text-lg mb-1">Location</div>
                        <div className="text-gray-600 dark:text-gray-400">Skid Row, Los Angeles</div>
                      </div>
                    </div>
                  </div>

                  {/* Donation Tiers - Future campaigns mode */}
                  <div id="donation-tiers-section" className="mb-16">
                    <DonationTiers />
                  </div>

                  {/* Continue Button - Sticky on mobile */}
                  {amount > 0 && (
                    <div className="fixed bottom-0 left-0 right-0 p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-lg md:relative md:p-0 md:bg-transparent md:dark:bg-transparent md:border-0 md:shadow-none md:text-center z-50">
                      <button
                        onClick={handleContinueToDetails}
                        className="w-full md:w-auto bg-[#8B3A3A] hover:bg-[#6B2A2A] text-white font-bold py-4 md:py-6 px-8 md:px-16 rounded-xl md:rounded-2xl text-lg md:text-2xl shadow-lg md:shadow-2xl md:hover:shadow-3xl md:transform md:hover:scale-105 transition-all duration-300"
                      >
                        Continue with ${amount.toFixed(2)} →
                      </button>
                    </div>
                  )}
                  {/* Spacer for fixed button on mobile */}
                  {amount > 0 && <div className="h-20 md:hidden"></div>}
                </>
              ) : (
                <>
                  {/* ============================================================================ */}
                  {/* ACTIVE CAMPAIGN MODE - Shown when CAMPAIGN_MODE = 'active' */}
                  {/* ============================================================================ */}
                  {/* Hero Section */}
                  <div className="text-center mb-16">
                    <h1
                      className="text-5xl md:text-7xl font-bold mb-6 font-heading text-primary dark:text-white"
                    >
                      Feed Skid Row in Los Angeles This Thanksgiving
                    </h1>
                    <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-8 font-bold">
                      Help us serve deep fried lobster tails.
                    </p>
                    <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed font-bold">
                      On Thanksgiving morning, Thursday, November 27th, 2025, we're bringing dignity and joy to Skid Row, Los Angeles,
                      by serving premium deep-fried lobster tails to those unhoused.
                      Every donation makes a real difference!
                    </p>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-16">
                    <ProgressBar />
                  </div>

                  {/* Campaign Info */}
                  <div
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-10 mb-16"
                    style={{ border: '3px solid #DAA520' }}
                  >
                    <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center font-heading">
                      Campaign Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                      <div className="flex flex-col items-center text-center">
                        <div className="bg-[#8B3A3A] rounded-full p-4 mb-4">
                          <Heart className="w-8 h-8 text-white" />
                        </div>
                        <div className="font-semibold text-gray-900 dark:text-white text-lg mb-1">Cost Per Tail</div>
                        <div className="text-gray-600 dark:text-gray-400">$8.40 per lobster tail</div>
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <div className="bg-[#8B3A3A] rounded-full p-4 mb-4">
                          <Heart className="w-8 h-8 text-white" />
                        </div>
                        <div className="font-semibold text-gray-900 dark:text-white text-lg mb-1">Retail Value</div>
                        <div className="text-gray-600 dark:text-gray-400">$5,077.15 worth of food</div>
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <div className="bg-[#8B3A3A] rounded-full p-4 mb-4">
                          <Heart className="w-8 h-8 text-white" />
                        </div>
                        <div className="font-semibold text-gray-900 dark:text-white text-lg mb-1">Event Date</div>
                        <div className="text-gray-600 dark:text-gray-400">Thanksgiving Morning, Nov 27</div>
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <div className="bg-[#8B3A3A] rounded-full p-4 mb-4">
                          <Heart className="w-8 h-8 text-white" />
                        </div>
                        <div className="font-semibold text-gray-900 dark:text-white text-lg mb-1">Location</div>
                        <div className="text-gray-600 dark:text-gray-400">Skid Row, Los Angeles</div>
                      </div>
                    </div>
                  </div>

                  {/* Donation Tiers */}
                  <div id="donation-tiers-section" className="mb-16">
                    <DonationTiers />
                  </div>

                  {/* Continue Button - Sticky on mobile */}
                  {amount > 0 && (
                    <div className="fixed bottom-0 left-0 right-0 p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-lg md:relative md:p-0 md:bg-transparent md:dark:bg-transparent md:border-0 md:shadow-none md:text-center z-50">
                      <button
                        onClick={handleContinueToDetails}
                        className="w-full md:w-auto bg-[#8B3A3A] hover:bg-[#6B2A2A] text-white font-bold py-4 md:py-6 px-8 md:px-16 rounded-xl md:rounded-2xl text-lg md:text-2xl shadow-lg md:shadow-2xl md:hover:shadow-3xl md:transform md:hover:scale-105 transition-all duration-300"
                      >
                        Continue with ${amount.toFixed(2)} →
                      </button>
                    </div>
                  )}
                  {/* Spacer for fixed button on mobile */}
                  {amount > 0 && <div className="h-20 md:hidden"></div>}
                </>
              )}
            </>
          )}

          {step === 'details' && (
            <div className="max-w-2xl mx-auto">
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-10 text-center font-heading">
                Your Information
              </h2>

              {/* Summary Card */}
              <div className="bg-gradient-to-br from-[#8B3A3A] to-[#6B2A2A] rounded-2xl shadow-2xl p-8 mb-10">
                <div className="text-center">
                  <div className="text-lg mb-2 !text-white">Your Donation</div>
                  <div className="text-6xl font-bold mb-3 !text-white">${amount.toFixed(2)}</div>

                  {getTailsFunded(amount) >= 1 ? (
                    <div className="text-xl !text-white">
                      Funding <span className="font-bold !text-[#DAA520]">{getTailsFunded(amount)}</span> lobster {getTailsFunded(amount) === 1 ? 'tail' : 'tails'}
                    </div>
                  ) : (
                    <div className="mt-2">
                      <div className="text-lg !text-white/90 mb-3">
                        We appreciate your contribution — every dollar counts!
                      </div>
                      <div className="text-sm !text-white/70 mb-4">
                        Presently funding 0 lobster tails
                      </div>
                      <button
                        onClick={() => {
                          setAmount(8.40)
                          setStep('select')
                          setTimeout(() => {
                            document.getElementById('donation-tiers-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                          }, 100)
                        }}
                        className="inline-flex items-center px-4 py-2 bg-[#DAA520] hover:bg-[#B8860B] text-white font-semibold rounded-lg transition-colors text-sm"
                      >
                        <span className="mr-2">✨</span>
                        Upgrade to $8.40 to fund 1 tail
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Email Input */}
              <div id="email-input-section" className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-8">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={donorEmail}
                  onChange={(e) => {
                    setDonorEmail(e.target.value)
                    setEmailError(null)
                  }}
                  placeholder="your@email.com"
                  className={`w-full px-4 py-4 border-2 rounded-xl focus:outline-none bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-lg ${
                    emailError
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-300 dark:border-gray-600 focus:border-[#8B3A3A]'
                  }`}
                />
                {emailError && <p className="mt-2 text-sm text-red-500">{emailError}</p>}
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Required for your donation receipt</p>
              </div>

              {/* Name Input */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-8">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Your Name <span className="text-gray-400">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={donorName}
                  onChange={(e) => setDonorName(e.target.value)}
                  placeholder="Leave blank if anonymous"
                  className="w-full px-4 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:border-[#8B3A3A] focus:outline-none bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-lg"
                />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Leave blank if you'd like to remain anonymous</p>
              </div>

              {/* Total Summary */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-8 mb-10">
                <div className="flex justify-between items-center text-lg mb-3">
                  <span className="text-gray-700 dark:text-gray-300">Donation Amount:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">${amount.toFixed(2)}</span>
                </div>
                {tipAmount > 0 && (
                  <div className="flex justify-between items-center text-lg mb-3">
                    <span className="text-gray-700 dark:text-gray-300">Tip:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">${tipAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-gray-300 dark:border-gray-600 pt-3 mt-3">
                  <div className="flex justify-between items-center text-2xl font-bold">
                    <span className="text-gray-900 dark:text-white">Total:</span>
                    <span className="text-[#8B3A3A]">${totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons - Sticky on mobile to stay above keyboard */}
              <div className="fixed bottom-0 left-0 right-0 p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-lg md:relative md:p-0 md:bg-transparent md:dark:bg-transparent md:border-0 md:shadow-none z-50">
                <div className="flex space-x-4 max-w-2xl mx-auto">
                  <button
                    onClick={() => setStep('select')}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-bold py-4 px-6 rounded-xl transition-colors text-lg"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleContinueToPayment}
                    disabled={isCreatingReservation}
                    className="flex-1 bg-[#8B3A3A] hover:bg-[#6B2A2A] text-white font-bold py-4 px-6 rounded-xl transition-colors text-lg disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isCreatingReservation ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      'Continue to Payment →'
                    )}
                  </button>
                </div>
              </div>
              {/* Spacer for fixed button on mobile */}
              <div className="h-24 md:hidden"></div>
            </div>
          )}

          {step === 'payment' && reservation && (
            <div className="max-w-2xl mx-auto">
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-10 text-center font-heading">
                Complete Your Donation
              </h2>

              <ReservationTimer expiresAt={reservation.expires_at} onExpire={handleReservationExpire} />

              {/* Payment Summary */}
              <div className="bg-gradient-to-br from-[#8B3A3A] to-[#6B2A2A] rounded-2xl shadow-2xl p-8 mb-10">
                <div className="text-center">
                  <div className="text-lg mb-2 !text-white">Total Amount</div>
                  <div className="text-6xl font-bold mb-3 !text-white">${totalAmount.toFixed(2)}</div>

                  {getTailsFunded(amount) >= 1 ? (
                    <div className="text-xl !text-white">
                      Funding <span className="font-bold !text-[#DAA520]">{getTailsFunded(amount)}</span> lobster {getTailsFunded(amount) === 1 ? 'tail' : 'tails'}
                    </div>
                  ) : (
                    <div className="mt-2">
                      <div className="text-lg !text-white/90 mb-3">
                        We appreciate your contribution — every dollar counts!
                      </div>
                      <div className="text-sm !text-white/70 mb-4">
                        Presently funding 0 lobster tails
                      </div>
                      <button
                        onClick={() => {
                          setAmount(8.40)
                          setStep('select')
                          // Scroll to donation tiers after step change
                          setTimeout(() => {
                            document.getElementById('donation-tiers-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                          }, 100)
                        }}
                        className="inline-flex items-center px-4 py-2 bg-[#DAA520] hover:bg-[#B8860B] text-white font-semibold rounded-lg transition-colors text-sm"
                      >
                        <span className="mr-2">✨</span>
                        Upgrade to $8.40 to fund 1 tail
                      </button>
                    </div>
                  )}

                  {donorEmail && (
                    <div className="mt-4 text-sm !text-white/80">Receipt will be sent to: {donorEmail}</div>
                  )}
                </div>
              </div>

              {/* Tip Selector - in payment step so users don't miss it */}
              <div className="mb-8">
                <TipSelector subtotal={amount} currentTip={tipAmount} onTipChange={setTipAmount} />
              </div>

              {/* Payment Form */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
                <DonationPaymentForm
                  containerId="donation-card-container"
                  onPaymentComplete={() => handleProcessPayment('card')}
                  onApplePayComplete={() => handleProcessPayment('apple')}
                  onGooglePayComplete={() => handleProcessPayment('google')}
                  isProcessing={isProcessing}
                  buttonText={`Donate $${totalAmount.toFixed(2)}`}
                />
              </div>
            </div>
          )}

          {step === 'success' && donationNumber && (
            <div className="max-w-[800px] mx-auto px-6 md:px-0">
              {/* Main Confirmation Card - Burgundy Hero */}
              <div className="bg-gradient-to-br from-[#8B3A3A] to-[#6B2A2A] rounded-2xl shadow-2xl p-12 mb-8 text-center">
                {/* White Heart Icon */}
                <div className="flex items-center justify-center mb-6">
                  <Heart className="w-16 h-16 !text-white !fill-white" />
                </div>

                {/* Thank You Heading */}
                <h2 className="text-5xl md:text-6xl font-bold !text-white mb-6 font-heading">
                  Thank You!
                </h2>

                {/* Impact Statement */}
                <p className="text-lg md:text-xl !text-white leading-relaxed">
                  {getTailsFunded(amount) >= 1 ? (
                    <>
                      Your donation of <span className="font-bold !text-white">${amount.toFixed(2)}</span> will help feed{' '}
                      <span className="font-bold !text-white">{getTailsFunded(amount)}</span> {getTailsFunded(amount) === 1 ? 'person' : 'people'} {CAMPAIGN_MODE === 'future' ? 'at a future event' : 'this Thanksgiving'}.
                    </>
                  ) : (
                    <>
                      Your donation of <span className="font-bold !text-white">${amount.toFixed(2)}</span> is helping us reach our goal.{' '}
                      <span className="font-bold !text-white">Every dollar counts!</span>
                    </>
                  )}
                </p>
              </div>

              {/* Donation Details & Receipt Card */}
              <div className="bg-white dark:bg-gray-800 border-2 border-[#DAA520] rounded-xl shadow-lg p-8 mb-8 text-center">
                {/* Donation Number */}
                <div className="mb-6">
                  <div className="text-xs font-semibold tracking-wide uppercase text-gray-600 dark:text-gray-400 mb-2">
                    Donation Number
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                    {donationNumber}
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200 dark:border-gray-700 my-6"></div>

                {/* Receipt Information */}
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  A receipt has been sent to <span className="font-semibold">{donorEmail}</span>
                </p>
              </div>

              {/* What Happens Next Section */}
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-8 mb-12">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  What Happens Next?
                </h3>

                <div className="space-y-4 text-left">
                  <div className="flex items-start">
                    <Heart className="w-5 h-5 text-[#8B3A3A] dark:text-[#DAA520] mt-0.5 mr-3 flex-shrink-0 fill-[#8B3A3A] dark:fill-[#DAA520]" />
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      Your donation has been processed successfully.
                    </p>
                  </div>

                  <div className="flex items-start">
                    <Heart className="w-5 h-5 text-[#8B3A3A] dark:text-[#DAA520] mt-0.5 mr-3 flex-shrink-0 fill-[#8B3A3A] dark:fill-[#DAA520]" />
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      You'll receive an email receipt shortly.
                    </p>
                  </div>

                  <div className="flex items-start">
                    <Heart className="w-5 h-5 text-[#8B3A3A] dark:text-[#DAA520] mt-0.5 mr-3 flex-shrink-0 fill-[#8B3A3A] dark:fill-[#DAA520]" />
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      {CAMPAIGN_MODE === 'future' 
                        ? 'Your donation will help fund our next charitable event serving gourmet lobster tails to people in need.'
                        : 'On Thanksgiving morning, we\'ll serve gourmet lobster tails to people in need at Skid Row.'}
                    </p>
                  </div>

                  <div className="flex items-start">
                    <Heart className="w-5 h-5 text-[#8B3A3A] dark:text-[#DAA520] mt-0.5 mr-3 flex-shrink-0 fill-[#8B3A3A] dark:fill-[#DAA520]" />
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      {CAMPAIGN_MODE === 'future'
                        ? 'Follow us on social media @tailtations for announcements about upcoming events.'
                        : 'Follow us on social media @tailtations for photos and updates from the event.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Return to Home CTA */}
              <div className="text-center">
                <button
                  onClick={() => navigate('/')}
                  className="bg-[#8B3A3A] hover:bg-[#7D4545] !text-white font-semibold py-4 px-8 rounded-lg transition-all duration-200 ease-in-out transform hover:scale-105 text-base"
                >
                  Return to Home
                </button>
              </div>
            </div>
          )}
          </div>
        </main>

        {/* Tax Disclaimer - Above Footer */}
        <div className="bg-yellow-50 dark:bg-yellow-900/30 border-t border-yellow-200 dark:border-yellow-800 py-4 relative z-10">
          <div className="container mx-auto px-4 text-center">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              <AlertCircle className="w-4 h-4 inline-block mr-2" />
              <strong>Important:</strong> Tailtations LLC is not a 501(c)(3) organization. Donations are not tax-deductible.
            </p>
          </div>
        </div>

        {/* Global Footer */}
        <Footer />
      </div>
    </>
  )
}

export default DonatePage
